/* ═══ CAMPAIGNS ═══
   The pure rules behind the Campaigns tab (CRM v3 slice 5): the status
   machine, the house-style guard, the brief validator, and the small pieces of
   arithmetic both the browser and `tools/crm/campaign.mjs` need.

   No React, no Firestore, no fetch — the node writer imports this file
   directly, and the callable's TypeScript port (functions/src/services/) keeps
   the same rules on the server. Where a rule exists in two places, the comment
   says so; keep them in step.

   The one thing this file will not do is send. A campaign's status can say
   `scheduled`, but only functions/src/handlers/crm/campaignSend.ts ever talks
   to Mailchimp, and only for an admin sitting in the Suite (plan decision 2).  */

import { isBulkEligible } from "./consent.js";

/* ── the vocabulary ──────────────────────────────────────────────────────── */

/** Every status a campaign record may hold, in lifecycle order. */
export const CAMPAIGN_STATUSES = {
  draft: "Draft",
  review: "Awaiting approval",
  approved: "Approved",
  scheduled: "Scheduled",
  sent: "Sent",
  measured: "Measured",
  discarded: "Discarded",
};

/** The three shapes an initiative takes. `post` covers an article or a social
 *  post that serves a campaign; it never goes through Mailchimp. */
export const CAMPAIGN_CHANNELS = {
  email: "Email",
  one_to_one: "One-to-one",
  post: "Post",
};

/** The colour token each status pill uses, by name in src/lib/styles.js `C`. */
export const CAMPAIGN_STATUS_TONE = {
  draft: "amber",
  review: "indigo",
  approved: "blue",
  scheduled: "teal",
  sent: "green",
  measured: "stone",
  discarded: "muted",
};

/** The one from-address every bulk send uses. */
export const CAMPAIGN_FROM = "ivin@newyorkcapture.com";
export const CAMPAIGN_FROM_NAME = "Ivin Ballen";
export const CAMPAIGN_TIMEZONE = "America/New_York";

/** `cp_<epoch ms>_<rand>` — the same id shape contacts and clients use, so two
 *  campaigns written in the same millisecond cannot collide. */
export const makeCampaignId = (now = Date.now(), rand = Math.random().toString(36).slice(2, 8)) =>
  `cp_${now}_${rand}`;

/**
 * Is this record a CRM v3 campaign?
 *
 * The `campaigns` collection is not new: nyc-suite's retired 3-step Mailchimp
 * wizard wrote `camp_<epoch>` records into it with a completely different
 * shape (recipientIds, statusFilter, includeOrgs, no channel). Those records
 * are still there, and reading one as a v3 campaign would show a table row
 * with no audience, no brief and a status the machine does not know. So the
 * tab reads only records that carry BOTH the v3 id prefix and a known channel.
 * Nothing migrates them; they are simply not v3 campaigns.
 */
export function isV3Campaign(record) {
  return !!record
    && /^cp_/.test(String(record.id || ""))
    && Object.prototype.hasOwnProperty.call(CAMPAIGN_CHANNELS, String(record.channel || ""));
}

/* ── the status machine ──────────────────────────────────────────────────── */

/**
 * Every legal move, as {action: {from: to}}.
 *
 * Read it as the sentence the plan writes: a draft becomes `review` when
 * somebody opens it, approving freezes the audience and makes it `approved`,
 * an approved campaign is scheduled or sent, the first results sync moves a
 * sent campaign to `measured`, and anything at all can be discarded.
 *
 * Only ONE move goes backwards: editing the copy of an approved campaign
 * returns it to `review`, because the audience thaws with it and the person
 * who approved a different email has not approved this one. Editing the copy
 * of a scheduled campaign is not a state change — it is refused, because
 * Mailchimp already holds the content and the drawer hides Edit there.
 */
export const CAMPAIGN_TRANSITIONS = {
  open: { draft: "review" },
  approve: { review: "approved" },
  schedule: { approved: "scheduled" },
  send: { approved: "sent", scheduled: "sent" },
  measure: { sent: "measured" },
  edit_copy: { approved: "review" },
  discard: {
    draft: "discarded", review: "discarded", approved: "discarded",
    scheduled: "discarded", sent: "discarded", measured: "discarded",
  },
};

export const CAMPAIGN_ACTIONS = Object.keys(CAMPAIGN_TRANSITIONS);

/** A record's status, floored to `draft` when it holds something unknown —
 *  the read path, where a malformed value must not read as further along than
 *  it is. */
export function campaignStatus(campaign) {
  const s = String(campaign?.status || "");
  return Object.prototype.hasOwnProperty.call(CAMPAIGN_STATUSES, s) ? s : "draft";
}

/**
 * The status this action produces, or `null` when the action does not apply
 * from here.
 *
 * Null rather than a throw: every caller is a click handler or a server sync
 * that has a sensible "nothing to do" branch, and a campaign already in the
 * target state is the common case (a second results sync, a drawer reopened).
 */
export function nextStatus(campaign, action) {
  const from = campaignStatus(campaign);
  const table = CAMPAIGN_TRANSITIONS[String(action || "")];
  if (!table) return null;
  const to = table[from];
  return to && to !== from ? to : null;
}

/** May the copy still be edited? Once Mailchimp holds the content, no. */
export const canEditCopy = (campaign) => ["draft", "review", "approved"].includes(campaignStatus(campaign));

/** Is this campaign waiting on Ivin? The approval bar shows for exactly these. */
export const awaitingApproval = (campaign) => ["draft", "review"].includes(campaignStatus(campaign));

/* ── house style ─────────────────────────────────────────────────────────── */

/* Ivin's rules for anything a client reads (feedback-no-em-dashes-no-editorial,
   feedback-answer-then-stop). The template adds the unsubscribe line and the
   studio address itself, so a body that writes its own is both duplicated and
   wrong, and "click here" is the link text of every phishing email ever sent.

   This is a REFUSAL, not a warning: campaign.mjs will not write a draft that
   breaks it. The check is on the copy the chat produces, never on the
   template's own footer. */
const HOUSE_STYLE_RULES = [
  { re: /—/, problem: 'an em dash ("—"). Use a comma, a period, or two sentences.' },
  { re: /\bunsubscrib/i, problem: 'the word "unsubscribe". The template adds the unsubscribe line and the studio address.' },
  { re: /\bclick here\b/i, problem: '"click here". Say what the link is.' },
];

/** The house-style problems in one piece of copy, as sentences. */
export function houseStyleProblems(text, label = "the body") {
  const s = String(text ?? "");
  return HOUSE_STYLE_RULES.filter((r) => r.re.test(s)).map((r) => `${label} contains ${r.problem}`);
}

/* ── the email's design fields ───────────────────────────────────────────── */

const trimmed = (v) => String(v ?? "").trim();

/** The optional text fields the branded email template draws, beside subject,
 *  body and the CTA. Kept in step with CampaignCopy in
 *  functions/src/services/campaignTemplate.ts. `details` is the table. */
export const COPY_DESIGN_TEXT_FIELDS = ["preheader", "tag", "eyebrow", "heroImageUrl", "heroAlt", "ctaNote"];

/** The most detail rows the template draws. Same as MAX_DETAIL_ROWS there. */
export const MAX_COPY_DETAILS = 8;

/**
 * The design fields of a copy object, normalised: trimmed strings, details as
 * {label, value} with empty rows dropped. Only fields that hold something are
 * returned, so a campaign without them keeps exactly the shape it had.
 */
export function copyDesignFields(copy) {
  const c = copy || {};
  const out = {};
  for (const k of COPY_DESIGN_TEXT_FIELDS) {
    const v = trimmed(c[k]);
    if (v) out[k] = v;
  }
  const details = Array.isArray(c.details)
    ? c.details
      .map((d) => {
        const row = { label: trimmed(d?.label), value: trimmed(d?.value) };
        const url = trimmed(d?.url);
        if (url) row.url = url;
        return row;
      })
      .filter((d) => d.label || d.value)
    : [];
  if (details.length) out.details = details;
  return out;
}

/* ── validation ──────────────────────────────────────────────────────────── */

/**
 * Everything wrong with a campaign brief, as a list of sentences. `[]` means
 * it is writable.
 *
 * The segment is NOT validated here — that is `validateSegment` in
 * src/lib/segments.js, and campaign.mjs runs both. This function owns the
 * campaign's own fields.
 *
 * @param input {name, channel, brief:{goal, angle, exclusions, followUp, evidence},
 *               copy:{subject, body, ctaText, ctaUrl, preheader?, tag?, eyebrow?,
 *                     heroImageUrl?, heroAlt?, details?:[{label, value}], ctaNote?},
 *               schedule:{at}}
 */
export function validateBrief(input) {
  const out = [];
  const c = input || {};
  const brief = c.brief || {};
  const copy = c.copy || {};

  if (!trimmed(c.name)) out.push("name is required.");
  const channel = trimmed(c.channel);
  if (!Object.prototype.hasOwnProperty.call(CAMPAIGN_CHANNELS, channel)) {
    out.push(`channel must be one of ${Object.keys(CAMPAIGN_CHANNELS).join(", ")}; got ${JSON.stringify(c.channel ?? null)}.`);
  }
  if (!trimmed(brief.goal)) out.push("brief.goal is required — what this outreach is for, in a sentence.");

  if (!Array.isArray(brief.exclusions ?? [])) out.push("brief.exclusions must be a list of {rule, count}.");
  if (!Array.isArray(brief.evidence ?? [])) out.push("brief.evidence must be a list of {kind, id, label}.");

  out.push(...validateCopy(copy, channel));

  const at = trimmed(c.schedule?.at);
  if (at && Number.isNaN(Date.parse(at))) out.push(`schedule.at is not a date: ${JSON.stringify(c.schedule.at)}.`);

  return out;
}

/**
 * Everything wrong with one campaign's copy, for its channel. validateBrief
 * runs it on a new draft; campaign.mjs --update-copy runs it on a replacement.
 */
export function validateCopy(copy, channel = "email") {
  const out = [];
  const c = copy || {};
  /* An email has to have something to send. A one-to-one sequence and a post
     do not: the copy for those is written per person, or lives on the site. */
  if (trimmed(channel) === "email") {
    if (!trimmed(c.subject)) out.push("copy.subject is required for an email campaign.");
    if (!trimmed(c.body)) out.push("copy.body is required for an email campaign.");
  }
  out.push(...houseStyleProblems(c.body, "copy.body"));
  out.push(...houseStyleProblems(c.subject, "copy.subject"));
  out.push(...copyDesignProblems(c));

  if (trimmed(c.ctaText) && !trimmed(c.ctaUrl)) out.push("copy.ctaText was given without copy.ctaUrl.");
  if (trimmed(c.ctaUrl) && !/^https?:\/\//i.test(trimmed(c.ctaUrl))) {
    out.push("copy.ctaUrl must be an http(s) URL.");
  }
  return out;
}

/**
 * Everything wrong with the design fields of one copy object. Shared by
 * validateBrief and the chat's --update-copy path. The house-style guard
 * applies to every piece of text a reader sees, not just the body.
 */
export function copyDesignProblems(copy) {
  const out = [];
  const c = copy || {};
  for (const k of COPY_DESIGN_TEXT_FIELDS) {
    if (c[k] !== undefined && c[k] !== null && typeof c[k] !== "string") out.push(`copy.${k} must be text.`);
  }
  for (const k of ["preheader", "tag", "eyebrow", "heroAlt", "ctaNote"]) {
    out.push(...houseStyleProblems(c[k], `copy.${k}`));
  }
  const hero = trimmed(c.heroImageUrl);
  if (hero && !/^https:\/\/\S+$/i.test(hero)) out.push("copy.heroImageUrl must be an https URL.");

  if (c.details !== undefined && c.details !== null) {
    if (!Array.isArray(c.details)) {
      out.push("copy.details must be a list of {label, value}.");
    } else {
      if (c.details.length > MAX_COPY_DETAILS) {
        out.push(`copy.details has ${c.details.length} rows; the email draws at most ${MAX_COPY_DETAILS}.`);
      }
      c.details.forEach((d, i) => {
        if (!d || typeof d !== "object" || typeof (d.label ?? "") !== "string" || typeof (d.value ?? "") !== "string") {
          out.push(`copy.details[${i}] must be {label, value} text.`);
          return;
        }
        out.push(...houseStyleProblems(d.label, `copy.details[${i}].label`));
        out.push(...houseStyleProblems(d.value, `copy.details[${i}].value`));
        if (d.url !== undefined && d.url !== null && d.url !== "" &&
            (typeof d.url !== "string" || !/^https:\/\/\S+$/i.test(d.url.trim()))) {
          out.push(`copy.details[${i}].url must be an https URL.`);
        }
      });
    }
  }
  return out;
}

/* ── copy versions ───────────────────────────────────────────────────────── */

/**
 * The campaign with this copy saved and the previous copy filed as a version.
 *
 * Versions are append-only and newest-last; the drawer draws them newest-first.
 * The status change (approved → review) is the caller's, through nextStatus,
 * because saving copy and thawing an audience are two different writes to
 * explain to the operator.
 */
export function appendCopyVersion(campaign, copy, { by = "", at = new Date().toISOString() } = {}) {
  const prior = campaign?.copy || {};
  const versions = Array.isArray(prior.versions) ? prior.versions : [];
  /* The design fields (hero, details, tag, ...) follow the same rule as the
     four below: an edit that names one replaces it, an edit that does not
     keeps the prior value. The Suite's editor only edits the four, so without
     this every save from it silently dropped the hero and the details. */
  const priorDesign = copyDesignFields(prior);
  const merged = { ...prior };
  for (const k of [...COPY_DESIGN_TEXT_FIELDS, "details"]) {
    if (copy && Object.prototype.hasOwnProperty.call(copy, k)) merged[k] = copy[k];
  }
  const design = copyDesignFields(merged);
  const next = {
    subject: trimmed(copy?.subject ?? prior.subject),
    body: String(copy?.body ?? prior.body ?? ""),
    ctaText: trimmed(copy?.ctaText ?? prior.ctaText),
    ctaUrl: trimmed(copy?.ctaUrl ?? prior.ctaUrl),
    ...design,
    versions,
  };
  /* Nothing actually changed: saving a version for an identical edit would
     fill the row with noise the operator has to read past. */
  const same = next.subject === trimmed(prior.subject)
    && next.body === String(prior.body ?? "")
    && next.ctaText === trimmed(prior.ctaText)
    && next.ctaUrl === trimmed(prior.ctaUrl)
    && JSON.stringify(design) === JSON.stringify(priorDesign);
  if (same) return campaign;

  next.versions = [
    ...versions,
    {
      n: versions.length + 1,
      by: trimmed(by) || "unknown",
      at,
      subject: trimmed(prior.subject),
      body: String(prior.body ?? ""),
      /* The CTA belongs in the version too. Without it, changing only the
         button — the commonest small edit there is — filed a version that
         looked identical to the new copy, and the link that was replaced was
         gone with no way back to it. A version is a copy you can return to or
         it is not a version. */
      ctaText: trimmed(prior.ctaText),
      ctaUrl: trimmed(prior.ctaUrl),
      ...priorDesign,
    },
  ];
  return { ...campaign, copy: next };
}

/* ── the audience ────────────────────────────────────────────────────────── */

/**
 * The counts the approval bar shows, from a resolver result.
 *
 * Deliberately recomputed from the CONTACTS, never read off the campaign: the
 * mockup's whole argument is that you approve the number the resolver returned
 * this second, not one the chat typed into the record. `isBulkEligible` is the
 * same function the server re-applies at send.
 */
export function audienceCounts(resolved) {
  const members = resolved?.members || [];
  const eligible = resolved?.eligible || members.filter(isBulkEligible ? (c) => isBulkEligible(c?.consent) : () => false);
  const counts = { total: members.length, opted_in: 0, relationship: 0, no_bulk: 0, unsubscribed: 0, eligible: eligible.length };
  for (const m of members) {
    const consent = m?.consent || {};
    if (consent.unsubscribed) counts.unsubscribed++;
    if (isBulkEligible(consent)) counts[consent.state === "opted_in" ? "opted_in" : "relationship"]++;
    else counts.no_bulk++;
  }
  return counts;
}

/**
 * Freeze an audience onto the record: the ids that were eligible at approval,
 * the moment they were frozen, and the split as it stood.
 *
 * ONLY the eligible list is frozen. A no-bulk member is visible in the drawer
 * so the operator can see who is being left out, and is never an id on the
 * record, because the record is the proof of who was approved to receive it.
 */
export function freezeAudience(campaign, resolved, { at = new Date().toISOString() } = {}) {
  const eligible = resolved?.eligible || [];
  const counts = audienceCounts(resolved);
  return {
    ...campaign,
    audience: {
      ...(campaign?.audience || {}),
      frozenAt: at,
      contactIds: eligible.map((c) => String(c?.id || "")).filter(Boolean),
      counts: {
        total: counts.total,
        opted_in: counts.opted_in,
        relationship: counts.relationship,
        no_bulk: counts.no_bulk,
        held_back: Number(resolved?.heldBack?.length ?? resolved?.heldBack ?? 0) || 0,
      },
    },
  };
}

/** Thaw an approved audience: editing the copy un-approves the send, and a
 *  frozen list that no longer matches an approval is worse than none. */
export function thawAudience(campaign) {
  return {
    ...campaign,
    audience: { ...(campaign?.audience || {}), frozenAt: null, contactIds: [] },
  };
}

/* ── scheduling ──────────────────────────────────────────────────────────── */

/** The America/New_York offset in ms at this instant. */
function nyOffsetMs(utcMs) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: CAMPAIGN_TIMEZONE,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date(utcMs)).map((p) => [p.type, p.value]),
  );
  const asIfUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return asIfUTC - Math.floor(utcMs / 1000) * 1000;
}

/** The wall clock a UTC instant reads as in New York, as [y, m, d, hh, mm]. */
function nyWallClockOf(date) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: CAMPAIGN_TIMEZONE,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(date).map((x) => [x.type, x.value]),
  );
  return [+p.year, +p.month, +p.day, +p.hour, +p.minute];
}

/**
 * A wall-clock New York time as a UTC instant. THROWS for a time that does not
 * exist.
 *
 * Two passes, because the offset depends on the answer (the standard DST fixed
 * point), and then a round trip, because on one morning a year the fixed point
 * has no solution.
 *
 * **Spring forward.** On the second Sunday in March the clocks go 01:59 →
 * 03:00, so 02:30 never happens. The two-pass converged on 01:30 and returned
 * it without a word: the operator asked for 2:30 am and the campaign was
 * scheduled for 1:30. Silently moving a send an hour is not a rounding
 * question, it is a different decision than the one that was approved, so it
 * throws and the caller has to ask again.
 *
 * **Fall back.** On the first Sunday in November 01:00–01:59 happens twice.
 * Both instants are real, so there is nothing to refuse and a choice has to be
 * made: this returns the EARLIER one, the still-EDT reading, which is what the
 * two-pass converges on by itself. Earlier is the safer default for a send
 * window — a campaign goes out at the first moment that matches what was
 * approved, not an hour after it.
 */
export function nyWallClockToUtc(y, m, d, hh = 0, mm = 0) {
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  let ms = wall - nyOffsetMs(wall);
  ms = wall - nyOffsetMs(ms);
  const out = new Date(ms);

  const [ry, rm, rd, rh, rmin] = nyWallClockOf(out);
  if (ry !== y || rm !== m || rd !== d || rh !== hh || rmin !== mm) {
    const asked = `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")} `
      + `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    throw new Error(
      `${asked} is not a time in New York — the clocks go forward that morning and that hour does not happen. `
      + "Pick a time before 2 am or after 3 am.",
    );
  }
  return out;
}

/** {year, month, day, weekday} for an instant, in New York. */
export function nyDateParts(date) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: CAMPAIGN_TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit", weekday: "short",
    }).formatToParts(date).map((x) => [x.type, x.value]),
  );
  return { year: +p.year, month: +p.month, day: +p.day, weekday: p.weekday };
}

const WEEKEND = new Set(["Sat", "Sun"]);

/**
 * The schedule picker's default: the next weekday at 9:40 am New York.
 *
 * "Next" means the earliest weekday 9:40 that is at least half an hour away —
 * so a Tuesday at 8 am proposes this morning, a Tuesday at 10 am proposes
 * Wednesday, and a Saturday proposes Monday. The half-hour floor exists
 * because the callable sends immediately for anything inside five minutes, and
 * a default that quietly means "now" is not a default.
 */
export function defaultSendAt(now = new Date()) {
  const FLOOR_MS = 30 * 60 * 1000;
  for (let i = 0; i < 10; i++) {
    const base = new Date(now.getTime() + i * 86_400_000);
    const { year, month, day, weekday } = nyDateParts(base);
    if (WEEKEND.has(weekday)) continue;
    const at = nyWallClockToUtc(year, month, day, 9, 40);
    if (at.getTime() - now.getTime() >= FLOOR_MS) return at.toISOString();
  }
  return new Date(now.getTime() + 86_400_000).toISOString();
}

/**
 * Mailchimp only accepts schedule times on a quarter-hour boundary, and it
 * rejects the whole call rather than rounding. Round UP, never down: rounding
 * down can put the send in the past.
 *
 * Ported to functions/src/services/mailchimp.ts — keep the two in step.
 */
export function roundUpToQuarterHour(iso) {
  const ms = Date.parse(String(iso || ""));
  if (Number.isNaN(ms)) return "";
  const QUARTER = 15 * 60 * 1000;
  const rounded = Math.ceil(ms / QUARTER) * QUARTER;
  return new Date(rounded).toISOString();
}

/** "Tue Sep 9, 9:40 am" — the picker's own label, in New York. */
export function formatSendAt(iso) {
  const ms = Date.parse(String(iso || ""));
  if (Number.isNaN(ms)) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: CAMPAIGN_TIMEZONE, weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  }).format(new Date(ms)).replace(",", "").replace(/\s(AM|PM)/, (m, p) => ` ${p.toLowerCase()}`);
}

/** The `datetime-local` value for a picker, in New York wall-clock. */
export function toLocalInput(iso) {
  const ms = Date.parse(String(iso || ""));
  if (Number.isNaN(ms)) return "";
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: CAMPAIGN_TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date(ms)).map((x) => [x.type, x.value]),
  );
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** The shape a complete `datetime-local` value has. A half-typed one does not
 *  match, which is how the picker tells "still typing" from "impossible". */
export const LOCAL_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * A `datetime-local` value back to a UTC instant, read as New York. `""` when
 * the value is incomplete OR names a clock time that does not exist.
 *
 * It swallows the throw because this runs on every keystroke in the picker and
 * an event handler is no place for an exception. The caller tells the two
 * cases apart with LOCAL_INPUT_RE: a complete value that converts to nothing
 * is the impossible one, and is worth saying so about.
 */
export function fromLocalInput(value) {
  const m = LOCAL_INPUT_RE.exec(String(value || "").trim());
  if (!m) return "";
  try {
    return nyWallClockToUtc(+m[1], +m[2], +m[3], +m[4], +m[5]).toISOString();
  } catch {
    return "";
  }
}
